import { Box, Skeleton, Stack } from '@mui/material';

export const MarketplaceDetailLoader = () => {
	return (
		<Box className="grid grid-flow-row gap-6">
			{/* ===== HERO: 2-column layout ===== */}
			<Box className="grid grid-cols-1 md:grid-cols-3 gap-6">

				{/* ---- LEFT COLUMN: Details ---- */}
				<Box className="md:col-span-2 py-6 px-4 md:py-10 md:px-12 bg-gray-100 rounded-3xl">
					<Stack gap={1} direction="column">
						{/* Business name */}
						<Skeleton variant="text" sx={{ fontSize: '32px', width: '70%' }} />

						{/* Description — 3 lines */}
						<Box className="mt-1">
							<Skeleton variant="text" sx={{ fontSize: '14px', width: '100%' }} />
							<Skeleton variant="text" sx={{ fontSize: '14px', width: '95%' }} />
							<Skeleton variant="text" sx={{ fontSize: '14px', width: '80%' }} />
						</Box>

						{/* Progress bar */}
						<Box className="pt-6 pb-6">
							<Skeleton variant="rounded" sx={{ height: '28px', width: '100%', borderRadius: '10px' }} />
						</Box>

						{/* Co-fund CTA strip */}
						<Box className="flex items-center justify-between rounded-full bg-investor-bg p-1.5 pl-5">
							<Skeleton variant="text" sx={{ fontSize: '14px', width: '160px', bgcolor: 'rgba(255,255,255,0.15)' }} />
							<Skeleton variant="rounded" sx={{ width: '120px', height: '36px', borderRadius: '9999px', bgcolor: 'rgba(255,255,255,0.15)' }} />
						</Box>

						{/* Key metrics grid — 2 columns, 5 rows */}
						<Box className="grid grid-cols-1 md:grid-cols-2 gap-x-8 mt-4">
							{[1, 2, 3, 4, 5].map((row) => (
								<Box
									key={row}
									className="flex justify-between items-center py-3 border-b border-investor-deep">
									<Skeleton variant="text" sx={{ fontSize: '12px', width: '80px' }} />
									{row === 4 ? (
										/* Rating circle badge */
										<Skeleton variant="circular" width={28} height={28} />
									) : (
										<Skeleton variant="text" sx={{ fontSize: '14px', width: '60px' }} />
									)}
								</Box>
							))}
						</Box>
					</Stack>
				</Box>

				{/* ---- RIGHT COLUMN: Image + Business Info ---- */}
				<Box className="flex flex-col rounded-3xl">
					{/* Logo image */}
					<Skeleton
						variant="rectangular"
						sx={{ width: '100%', height: { xs: '256px', md: '640px' }, borderRadius: '24px 24px 0 0' }}
					/>

					{/* Business info sidebar (investor-mint panel) */}
					<Box className="flex-1 p-6 bg-investor-mint rounded-3xl -mt-10 relative">
						{/* Industry */}
						<Box className="flex flex-col py-3 border-b border-investor-deep">
							<Skeleton variant="text" sx={{ fontSize: '12px', width: '60px' }} />
							<Skeleton variant="text" sx={{ fontSize: '20px', width: '120px' }} />
						</Box>

						{/* Years Trading */}
						<Box className="flex flex-col py-3 border-b border-investor-deep">
							<Skeleton variant="text" sx={{ fontSize: '12px', width: '80px' }} />
							<Skeleton variant="text" sx={{ fontSize: '20px', width: '60px' }} />
						</Box>

						{/* Country */}
						<Box className="flex flex-col py-3">
							<Skeleton variant="text" sx={{ fontSize: '12px', width: '50px' }} />
							<Skeleton variant="text" sx={{ fontSize: '20px', width: '100px' }} />
						</Box>
					</Box>
				</Box>
			</Box>

			{/* ===== BOTTOM: Financial Ratios (left) + Latest Opportunities (right) ===== */}
			<Box className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
				{/* Financial Highlights — left col */}
				<Box className="md:col-span-2 bg-gray-100 rounded-3xl p-6">
					<Skeleton variant="text" sx={{ fontSize: '24px', width: '200px', mb: 2 }} />
					<Box className="grid grid-cols-2 md:grid-cols-3 gap-4">
						{[1, 2, 3, 4, 5, 6].map((card) => (
							<Box key={card} className="flex flex-col gap-1 p-4 bg-white rounded-2xl">
								<Skeleton variant="text" sx={{ fontSize: '12px', width: '80%' }} />
								<Skeleton variant="text" sx={{ fontSize: '20px', width: '60%' }} />
							</Box>
						))}
					</Box>
				</Box>

				{/* Latest Opportunities — right col */}
				<Box className="md:col-span-1 flex flex-col gap-4">
					<Box className="bg-gray-100 rounded-3xl p-6">
						<Skeleton variant="text" sx={{ fontSize: '18px', width: '160px', mb: 2 }} />
						{[1, 2, 3].map((item) => (
							<Box key={item} className="flex items-center gap-3 py-3 border-b border-gray-200 last:border-0">
								<Skeleton variant="circular" width={40} height={40} />
								<Box className="flex-1">
									<Skeleton variant="text" sx={{ fontSize: '14px', width: '80%' }} />
									<Skeleton variant="text" sx={{ fontSize: '12px', width: '50%' }} />
								</Box>
							</Box>
						))}
					</Box>

					{/* Disclaimers */}
					<Box className="flex flex-col gap-2">
						<Skeleton variant="text" sx={{ fontSize: '12px', width: '100%' }} />
						<Skeleton variant="text" sx={{ fontSize: '12px', width: '90%' }} />
						<Skeleton variant="text" sx={{ fontSize: '12px', width: '95%' }} />
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
